// Νέα κίνηση ταμείου: έξοδο / ανάληψη / κατάθεση μετρητών. Για οθόνη αφής: μεγάλα κουμπιά, ποσό με
// αριθμητικό πληκτρολόγιο, κατηγορίες ως πλακίδια. Ένα clientKey ανά άνοιγμα: διπλό πάτημα ή επανάληψη μετά
// από χαμένη απάντηση = η ΙΔΙΑ κίνηση (ο server την επιστρέφει, δεν γράφει δεύτερη).

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, Loader2, X } from 'lucide-react';
import { useBackdropDismiss } from '../../../utils/backdropDismiss';
import type { CashMovement, CashMovementKind } from '../../../types/registerReport';
import { cashMovementsApi, isDefiniteRejection, type CashMovementsConfig } from './api';
import { KIND_META, KIND_ORDER, centsToApi, newClientKey, parseAmountInput } from './model';
import { KindIcon, KIND_TONE } from './ui';

interface Props {
  storeId: number;
  config: CashMovementsConfig;
  initialKind: CashMovementKind;
  onClose: () => void;
  onSaved: (movement: CashMovement, replay: boolean) => void;
}

const NOTE_PLACEHOLDER: Record<CashMovementKind, string> = {
  expense: 'π.χ. ψωμιά, καθαριστικά',
  withdrawal: 'π.χ. πήρε ο ιδιοκτήτης για την τράπεζα',
  deposit: 'π.χ. ρέστα βάρδιας',
};

const input = 'h-12 w-full rounded-xl border border-line bg-surface-2 px-3.5 text-[15px] text-t1 placeholder:text-t4 focus:border-blue-500 focus:outline-none';

export default function CashMovementForm({ storeId, config, initialKind, onClose, onSaved }: Props) {
  const [kind, setKind] = useState<CashMovementKind>(initialKind);
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [showDoc, setShowDoc] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [supplierVat, setSupplierVat] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientKey = useRef(newClientKey());
  const backdrop = useBackdropDismiss(() => { if (!busy) onClose(); });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!busy) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [busy, onClose]);

  const cents = parseAmountInput(amount);
  const tooLarge = cents != null && cents > Math.round(config.maxAmount * 100);
  const expense = kind === 'expense';
  const vatDigits = supplierVat.replace(/[\s.-]/g, '').replace(/^EL/i, '');
  const vatInvalid = expense && vatDigits !== '' && !/^\d{9}$/.test(vatDigits);
  const needsCategory = expense && categoryId == null;
  const cashWarning = expense && cents != null && cents >= Math.round(config.cashWarnAmount * 100);
  const valid = cents != null && !tooLarge && !needsCategory && !vatInvalid;
  const amountError = amount.trim() !== '' && cents == null
    ? 'Γράψε ποσό, π.χ. 12,50'
    : tooLarge ? `Ως ${config.maxAmount.toLocaleString('el-GR')} € ανά κίνηση` : null;

  const submit = async () => {
    if (!valid || busy || cents == null) return;
    setBusy(true);
    setError(null);
    try {
      const out = await cashMovementsApi.create(storeId, {
        kind,
        amount: centsToApi(cents),
        categoryId: expense ? categoryId : null,
        note: note.trim() || undefined,
        supplierName: expense ? supplierName.trim() || undefined : undefined,
        supplierVat: expense ? vatDigits || undefined : undefined,
        documentNumber: expense ? documentNumber.trim() || undefined : undefined,
        clientKey: clientKey.current,
      });
      onSaved(out.movement, out.replay);
    } catch (e) {
      if (isDefiniteRejection(e)) {
        // Ο server απάντησε «όχι»: τίποτα δεν γράφτηκε — η επόμενη προσπάθεια είναι νέα.
        clientKey.current = newClientKey();
        setError(e instanceof Error ? e.message : 'Η κίνηση δεν καταχωρήθηκε');
      } else {
        setError('Δεν ήρθε απάντηση από τον server. Πάτησε ξανά «Καταχώριση» — δεν θα καταχωρηθεί δύο φορές.');
      }
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-0 sm:p-4" {...backdrop}>
      <div role="dialog" aria-modal="true" aria-label="Νέα κίνηση ταμείου" data-testid="cash-movement-form"
        className="flex max-h-full w-full max-w-[600px] flex-col overflow-hidden bg-surface-1 shadow-xl sm:max-h-[94vh] sm:rounded-2xl sm:border sm:border-line">
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <h3 className="min-w-0 flex-1 text-[19px] font-bold text-t1">Νέα κίνηση ταμείου</h3>
          <button type="button" aria-label="Κλείσιμο" disabled={busy} onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-2 text-t2 hover:bg-surface-3 disabled:opacity-40">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="grid min-h-0 flex-1 auto-rows-max gap-5 overflow-y-auto px-5 py-4"
          onSubmit={e => { e.preventDefault(); submit(); }}>
          <div role="radiogroup" aria-label="Είδος κίνησης" className="grid grid-cols-3 gap-2">
            {KIND_ORDER.map(k => {
              const selected = k === kind;
              return (
                <button key={k} type="button" role="radio" aria-checked={selected} data-testid={`cash-kind-${k}`}
                  onClick={() => { setKind(k); setError(null); }}
                  className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border-2 px-2 text-[15px] font-semibold transition-colors touch-manipulation ${selected
                    ? KIND_TONE[k].selected
                    : 'border-line bg-surface-1 text-t2 hover:border-line-strong'}`}>
                  <KindIcon kind={k} className="h-5 w-5" />
                  {KIND_META[k].label}
                </button>
              );
            })}
          </div>
          <p className="-mt-3 text-[13px] text-t3">{KIND_META[kind].hint}</p>

          <label className="grid gap-1.5">
            <span className="text-[13px] font-medium text-t2">Ποσό</span>
            <div className="relative">
              <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" autoFocus
                placeholder="0,00" aria-label="Ποσό σε ευρώ" data-testid="cash-amount"
                className="h-16 w-full rounded-xl border border-line bg-surface-2 pl-4 pr-12 text-[30px] font-bold tabular-nums text-t1 placeholder:text-t4 focus:border-blue-500 focus:outline-none" />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[22px] font-semibold text-t3">€</span>
            </div>
            {amountError && <span className="text-[13px] text-red-600 dark:text-red-400">{amountError}</span>}
          </label>

          {expense && (
            <div role="radiogroup" aria-label="Κατηγορία εξόδου" className="grid gap-1.5">
              <span className="text-[13px] font-medium text-t2">Κατηγορία</span>
              {config.categories.length === 0 ? (
                <span className="text-[13px] text-amber-700 dark:text-amber-300">Δεν υπάρχουν ενεργές κατηγορίες εξόδων.</span>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {config.categories.map(c => (
                    <button key={c.id} type="button" role="radio" aria-checked={categoryId === c.id}
                      data-testid={`cash-category-${c.id}`} onClick={() => setCategoryId(c.id)}
                      className={`min-h-[48px] rounded-xl border px-3 text-[14px] font-medium transition-colors touch-manipulation ${categoryId === c.id
                        ? 'border-blue-600 bg-blue-600/10 text-blue-700 dark:border-blue-400 dark:text-blue-300'
                        : 'border-line bg-surface-1 text-t2 hover:border-line-strong'}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="grid gap-1.5">
            <span className="text-[13px] font-medium text-t2">Σημείωση <span className="font-normal text-t4">(προαιρετικά)</span></span>
            <input value={note} onChange={e => setNote(e.target.value)} maxLength={200} placeholder={NOTE_PLACEHOLDER[kind]}
              data-testid="cash-note" className={input} />
          </label>

          {expense && (
            <div className="grid gap-3">
              <button type="button" aria-expanded={showDoc} onClick={() => setShowDoc(v => !v)}
                className="flex min-h-[44px] items-center gap-2 text-left text-[14px] font-medium text-blue-700 dark:text-blue-300">
                <ChevronDown className={`h-4 w-4 transition-transform ${showDoc ? 'rotate-180' : ''}`} />
                Στοιχεία παραστατικού προμηθευτή <span className="font-normal text-t4">(προαιρετικά, για τον λογιστή)</span>
              </button>
              {showDoc && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1.5 sm:col-span-2">
                    <span className="text-[13px] font-medium text-t2">Προμηθευτής</span>
                    <input value={supplierName} onChange={e => setSupplierName(e.target.value)} maxLength={120} className={input} />
                  </label>
                  <label className="grid gap-1.5">
                    <span className="text-[13px] font-medium text-t2">ΑΦΜ</span>
                    <input value={supplierVat} onChange={e => setSupplierVat(e.target.value)} inputMode="numeric" maxLength={14}
                      data-testid="cash-supplier-vat" className={input} />
                    {vatInvalid && <span className="text-[13px] text-red-600 dark:text-red-400">Ο ΑΦΜ θέλει 9 ψηφία</span>}
                  </label>
                  <label className="grid gap-1.5">
                    <span className="text-[13px] font-medium text-t2">Αρ. παραστατικού</span>
                    <input value={documentNumber} onChange={e => setDocumentNumber(e.target.value)} maxLength={50} className={input} />
                  </label>
                </div>
              )}
            </div>
          )}

          {cashWarning && (
            <div data-testid="cash-warning" className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[13px] text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Έξοδα από {config.cashWarnAmount.toLocaleString('el-GR')} € και πάνω που πληρώνονται με μετρητά μπορεί να μην
                αναγνωρίζονται φορολογικά. Αν δεν είσαι σίγουρος, ρώτα τον λογιστή.
              </span>
            </div>
          )}

          {error && (
            <div role="alert" data-testid="cash-form-error"
              className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[14px] text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </form>

        <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={busy}
            className="min-h-[52px] rounded-xl border border-line-strong bg-surface-1 px-5 text-[15px] font-semibold text-t2 hover:text-t1 disabled:opacity-50">
            Άκυρο
          </button>
          <button type="button" onClick={submit} disabled={!valid || busy} data-testid="cash-submit"
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-[15px] font-semibold text-white hover:bg-blue-700 disabled:bg-surface-3 disabled:text-t3">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}
            Καταχώριση
          </button>
        </div>
      </div>
    </div>
  );
}
